import React, { useState } from 'react';
import {
    ImageList,
    ImageListItem,
    Box,
    SxProps,
    Theme,
    useMediaQuery,
    useTheme,
    IconButton,
    Tooltip,
} from '@mui/material';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import 'react-photo-view/dist/react-photo-view.css';
import { Close } from '@mui/icons-material';

export type PhotoGridLayoutVariant = 'default' | 'portfolio';

interface PhotoGridProps {
    photos?: string[];
    columns?: number;
    rowHeight?: number;
    sx?: SxProps<Theme>;
    isEditMode?: boolean;
    onAddPhoto?: () => void;
    onEditClick?: (index: number) => void;
    /** `portfolio`: featured first image + side stack + remainder grid (Media tab only). */
    layoutVariant?: PhotoGridLayoutVariant;
}

export const PhotoGrid: React.FC<PhotoGridProps> = ({
    photos = [],
    columns = 3,
    rowHeight = 100,
    sx,
    isEditMode,
    onAddPhoto,
    onEditClick,
    layoutVariant = 'default',
}) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const [brokenImages, setBrokenImages] = useState<Record<number, boolean>>({});

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    const column = isXs ? 2 : columns;
    const imageHeight = rowHeight;

    const renderPhotoCell = (url: string, index: number, cellSx: SxProps<Theme>) => {
        const base = `${url}`;
        const src = isEditMode ? url : base;
        const srcSet = isEditMode ? undefined : `${base} , ${base}`;
        return (
            <Box
                key={index}
                onMouseEnter={() => setHoverIndex(index)}
                onMouseLeave={() => setHoverIndex(null)}
                sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                    ...cellSx,
                }}>
                <PhotoView
                    src={brokenImages[index] ? '/apple-touch-icon.png' : url}
                    triggers={['onClick', 'onDoubleClick']}>
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: 'inherit',
                            position: 'relative',
                            overflow: 'hidden',
                        }}>
                        <img
                            src={brokenImages[index] ? '/apple-touch-icon.png' : src}
                            srcSet={brokenImages[index] ? undefined : srcSet}
                            sizes="(max-width: 600px) 50vw, 33vw"
                            alt={`profile-${index}`}
                            loading="lazy"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                cursor: 'pointer',
                            }}
                            onError={() => {
                                setBrokenImages(prev => ({ ...prev, [index]: true }));
                            }}
                        />
                        {hoverIndex === index && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    backgroundColor: 'rgba(0,0,0,0.35)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'opacity 0.2s ease-in-out',
                                    pointerEvents: 'none',
                                }}>
                                <ZoomInIcon sx={{ color: 'white', fontSize: 30 }} />
                            </Box>
                        )}
                        {isEditMode && onEditClick && (
                            <Tooltip title="Edit photo">
                                <IconButton
                                    size="small"
                                    onClick={e => {
                                        e.stopPropagation();
                                        onEditClick(index);
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        top: 6,
                                        right: 6,
                                        bgcolor: 'rgba(0,0,0,0.5)',
                                        color: 'white',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                                    }}>
                                    <Close />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </PhotoView>
            </Box>
        );
    };

    if (layoutVariant === 'portfolio' && photos.length > 0) {
        const [featuredUrl, ...rest] = photos;
        const sideUrls = rest.slice(0, 2);
        const moreUrls = rest.slice(2);

        return (
            <PhotoProvider>
                <Box
                    sx={{
                        display: 'grid',
                        width: '100%',
                        gap: 2,
                        gridTemplateColumns: isMdUp ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
                        gridAutoRows: 'minmax(0, auto)',
                        mt: 0,
                        ...sx,
                    }}>
                    {renderPhotoCell(featuredUrl, 0, {
                        borderRadius: '12px',
                        overflow: 'hidden',
                        minHeight: { xs: 280, md: 500 },
                        gridColumn: { xs: '1 / -1', md: '1 / 3' },
                        gridRow: { xs: 'auto', md: '1 / 3' },
                    })}
                    {sideUrls.map((url, i) =>
                        renderPhotoCell(url, i + 1, {
                            borderRadius: '12px',
                            overflow: 'hidden',
                            minHeight: { xs: 160, md: 242 },
                            gridColumn: {
                                xs: sideUrls.length === 1 ? '1 / -1' : 'auto',
                                md: '3',
                            },
                            gridRow: { md: i === 0 ? '1' : '2' },
                        }),
                    )}
                    {moreUrls.length > 0 && (
                        <Box
                            sx={{
                                gridColumn: '1 / -1',
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: {
                                    xs: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(3, minmax(0, 1fr))',
                                },
                            }}>
                            {moreUrls.map((url, i) =>
                                renderPhotoCell(url, i + 1 + sideUrls.length, {
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    minHeight: { xs: 140, md: 200 },
                                }),
                            )}
                        </Box>
                    )}
                    {isEditMode && (
                        <Box
                            sx={{
                                gridColumn: '1 / -1',
                                minHeight: 120,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '12px',
                                border: '1px dashed',
                                borderColor: 'rgba(255,255,255,0.2)',
                            }}>
                            <IconButton onClick={onAddPhoto} sx={{ borderRadius: 2 }}>
                                <AddPhotoAlternateIcon sx={{ fontSize: 40, color: 'gray' }} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </PhotoProvider>
        );
    }

    if (layoutVariant === 'portfolio' && photos.length === 0 && !isEditMode) {
        return <Box sx={{ width: '100%', mt: 0, ...sx }} />;
    }

    if (layoutVariant === 'portfolio' && photos.length === 0 && isEditMode) {
        return (
            <Box
                sx={{
                    width: '100%',
                    mt: 0,
                    minHeight: 160,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px',
                    border: '1px dashed',
                    borderColor: 'rgba(255,255,255,0.2)',
                    ...sx,
                }}>
                <IconButton onClick={onAddPhoto} sx={{ borderRadius: 2 }}>
                    <AddPhotoAlternateIcon sx={{ fontSize: 40, color: 'gray' }} />
                </IconButton>
            </Box>
        );
    }

    return (
        <PhotoProvider>
            <ImageList
                sx={{
                    width: '100%',
                    mt: 1,
                    overflow: 'visible',
                    ...sx,
                }}
                cols={column}
                rowHeight={imageHeight}>
                {photos.map((url, index) => {
                    const base = `${url}`;
                    const src = isEditMode ? url : base;
                    const srcSet = isEditMode ? undefined : `${base} , ${base}`;

                    return (
                        <ImageListItem
                            key={index}
                            onMouseEnter={() => setHoverIndex(index)}
                            onMouseLeave={() => setHoverIndex(null)}
                            sx={{
                                width: '100%',
                                height: imageHeight,
                                position: 'relative',
                                cursor: 'pointer',
                            }}>
                            <PhotoView
                                src={brokenImages[index] ? '/apple-touch-icon.png' : url}
                                key={index}
                                triggers={['onClick', 'onDoubleClick']}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}>
                                    <img
                                        src={brokenImages[index] ? '/apple-touch-icon.png' : src}
                                        srcSet={brokenImages[index] ? undefined : srcSet}
                                        sizes="(max-width: 600px) 50vw, 33vw"
                                        alt={`profile-${index}`}
                                        loading="lazy"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: 4,
                                            display: 'block',
                                            cursor: 'pointer',
                                        }}
                                        onError={e => {
                                            setBrokenImages(prev => ({ ...prev, [index]: true }));
                                        }}
                                    />

                                    {/* Hover zoom hint */}
                                    {hoverIndex === index && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                backgroundColor: 'rgba(0,0,0,0.35)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 1,
                                                transition: 'opacity 0.2s ease-in-out',
                                                pointerEvents: 'none',
                                            }}>
                                            <ZoomInIcon sx={{ color: 'white', fontSize: 30 }} />
                                        </Box>
                                    )}

                                    {/* Edit button in edit mode */}
                                    {isEditMode && onEditClick && (
                                        <Tooltip title="Edit photo">
                                            <IconButton
                                                size="small"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    onEditClick(index);
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    right: 6,
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: 'white',
                                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                                                }}>
                                                <Close />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </PhotoView>
                        </ImageListItem>
                    );
                })}

                {/* Add Photo Button in Edit Mode */}
                {isEditMode && (
                    <ImageListItem
                        sx={{
                            width: '100%',
                            height: imageHeight,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <IconButton
                            onClick={onAddPhoto}
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2,
                            }}>
                            <AddPhotoAlternateIcon sx={{ fontSize: 40, color: 'gray' }} />
                        </IconButton>
                    </ImageListItem>
                )}
            </ImageList>
        </PhotoProvider>
    );
};
